import { createContext, ReactNode, useContext, useState } from "react";
import { useApi } from "./ApiContext";
import { Exception } from "@zxing/library";
import { useQuery } from "react-query";
import { Invoice, PaymentMethodEnum } from "../types/subscription";
import { InvoiceModel } from "../types/invoice";

type InvoiceByIdResult = {
	invoice: InvoiceModel | undefined;
	isLoading: boolean;
};

export type InvoiceContextType = {
	invoices: Invoice[] | undefined;
	totalInvoicesCount: number | undefined;
	isLoadingInvoices: boolean;
	page: number;
	setPage: (page: number) => void;
	size: number;
	setSize: (size: number) => void;
	getInvoiceById: (id: string) => InvoiceByIdResult;
};

export const InvoiceContext = createContext<InvoiceContextType | undefined>(undefined);

export const InvoiceContextProvider: React.FC<{ children: ReactNode | ReactNode[] }> = ({ children }) => {
	const { invoice: api } = useApi();

	const [page, setPage] = useState<number>(0);
	const [size, setSize] = useState<number>(10);

	const { data: invoicesResult, isLoading: isLoadingInvoices } = useQuery(
		["invoice-" + page + "-" + size],
		async () => {
			const fetchedInvoice = await api.getInvoices({
				page: page ?? 0,
				size: size ?? 10,
			});
			if (!fetchedInvoice.isSuccess) throw new Exception(fetchedInvoice.message);
			return fetchedInvoice.result;
		}
	);

	const invoices = invoicesResult?.data;
	const totalInvoicesCount = invoicesResult?.totalCount;

	function getInvoiceById(id: string) {
		const { data: invoice, isLoading } = useQuery(["invoice-" + id], async () => {
			const invoice = await api.getInvoiceById(id);
			return invoice.result;
		});

		return { invoice, isLoading };
	}

	return (
		<InvoiceContext.Provider
			value={{
				invoices,
				totalInvoicesCount,
				isLoadingInvoices,
				page,
				setPage,
				size,
				setSize,
				getInvoiceById,
			}}
		>
			{children}
		</InvoiceContext.Provider>
	);
};

function useInvoiceContext() {
	const context = useContext(InvoiceContext);
	if (!context) {
		throw new Error("useInvoiceContext() must be used inside a InvoiceContext provider");
	}
	return context;
}

export default useInvoiceContext;
